import {
  Box,
  HairlineCard,
  iconSize,
  palette,
  Stack,
  Typography,
  type LucideIcon,
} from '@munifocus/ui';

/**
 * What a switched-off module's screen shows when there is nothing in it yet.
 *
 * Before this, a town without tickets met an info line and then the whole
 * dashboard regardless: four $0.00 tiles and a twelve-month "Collected by
 * month" chart of $0.00. Empty scaffolding reads as "this is broken" or "we
 * collected nothing", never as "this is not on". So the rule for every module
 * screen: off *and* empty shows only this — what the module is, and who turns
 * it on. Off with data still on file (tickets issued before the plan changed)
 * keeps the usual screen under the plain notice, because that data is still
 * owed and still payable.
 */
export function ModuleOffNotice({
  icon: Icon,
  title,
  what,
  who,
}: {
  icon: LucideIcon;
  /** One line: the module is off here. */
  title: string;
  /** What the module does, in the officer's words. */
  what: string;
  /** Who switches it on, and how to ask. */
  who: string;
}) {
  return (
    <HairlineCard sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: 1,
            display: 'grid',
            placeItems: 'center',
            bgcolor: palette.brand.accentTint,
            color: palette.brand.accent,
          }}
        >
          <Icon size={iconSize.lg} />
        </Box>
        <Stack spacing={1} sx={{ maxWidth: '68ch' }}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {what}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {who}
          </Typography>
        </Stack>
      </Stack>
    </HairlineCard>
  );
}
